import { useCallback } from "react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { ExternalLink } from "lucide-react";
import type { IntakeData } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { BackButton } from "./CardPrimitives";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";
import { SubmitCelebrationCard } from "./SubmitCelebrationCard";

interface OtherSpecialtyThankYouCardProps {
  data: IntakeData;
  onBack: () => void;
}

export function OtherSpecialtyThankYouCard({
  data,
  onBack,
}: OtherSpecialtyThankYouCardProps) {
  const { _ } = useLingui();
  const websiteUrl =
    import.meta.env.VITE_JEMA_WEBSITE_URL || "https://jemaservices.com.co";

  const goToWebsite = useCallback(() => {
    window.location.href = websiteUrl;
  }, [websiteUrl]);

  useIntakeMobileChromeRegistration(
    () => ({
      onCancel: onBack,
      onContinue: goToWebsite,
      continueLabel: _(msg`Return to website`),
      continueShowKeyboardHint: false,
    }),
    [onBack, goToWebsite, _],
  );

  return (
    <div className="mx-auto w-full max-w-lg">
      <SubmitCelebrationCard
        data={{ firstName: data.firstName }}
        showVerifyHint={false}
        showInboxButton={false}
        descriptionContent={
          <Trans>
            <span className="block font-semibold text-[#166B93]">
              Your other-specialty inquiry has been sent.
            </span>
            <span className="mt-2 block">
              Thank you,{" "}
              <span className="font-semibold text-gray-900">{data.firstName ?? ""}</span>.
              We’ve shared your details with our team. Someone will follow up using
              the contact information you provided.
            </span>
          </Trans>
        }
      />

      {/* Back + CTA — md+: same row as CardActions; mobile: sticky footer only */}
      <div className="card-actions-row mt-8 hidden w-full max-w-lg flex-row flex-nowrap items-center gap-1.5 px-1 sm:gap-3 md:flex [&>button]:!m-0 mx-auto">
        <BackButton onClick={onBack} />
        <div className="min-w-0 flex-1 md:flex-initial md:w-auto md:min-w-0 [&>button]:!m-0">
          <Button
            type="button"
            size="lg"
            className="h-12 w-full min-w-[min(100%,200px)] rounded-2xl bg-[#166B93] px-6 text-base font-semibold shadow-lg shadow-[#166B93]/25 transition hover:bg-[#145d82] hover:shadow-xl hover:shadow-[#166B93]/30 md:w-auto md:px-8"
            onClick={goToWebsite}
          >
            <ExternalLink className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            <Trans>Return to website</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}
